import type { CommerceProduct, CommerceProductCategory } from "@teqtank/commerce-contracts";

export const mockProductCategories: readonly CommerceProductCategory[] = Object.freeze([
    Object.freeze({
        id: "daily-wellness",
        slug: "daily-wellness",
        name: "Daily Wellness",
        description: "Everyday products designed to support a consistent wellness routine.",
        parentId: null,
        image: Object.freeze({
            url: "https://placehold.co/1200x800?text=Daily+Wellness",
            alternativeText: "Daily Wellness products",
            width: 1200,
            height: 800,
        }),
        productCount: 3,
        sortOrder: 10,
    }),

    Object.freeze({
        id: "nutrition",
        slug: "nutrition",
        name: "Nutrition",
        description: "Nutrition products for daily performance and recovery.",
        parentId: null,
        image: Object.freeze({
            url: "https://placehold.co/1200x800?text=Nutrition",
            alternativeText: "Nutrition products",
            width: 1200,
            height: 800,
        }),
        productCount: 2,
        sortOrder: 20,
    }),

    Object.freeze({
        id: "bundles",
        slug: "bundles",
        name: "Bundles",
        description: "Curated combinations designed to simplify product selection.",
        parentId: null,
        image: Object.freeze({
            url: "https://placehold.co/1200x800?text=Bundles",
            alternativeText: "Product bundles",
            width: 1200,
            height: 800,
        }),
        productCount: 1,
        sortOrder: 30,
    }),
]);

export const mockProducts: readonly CommerceProduct[] = Object.freeze([
    createProduct({
        id: "daily-restore",
        displaySku: "TT-RESTORE-001",
        slug: "daily-restore",
        name: "Daily Restore",
        shortDescription: "A balanced daily wellness blend.",
        description:
            "Daily Restore is designed as a simple foundation for a consistent daily wellness routine.",
        categoryIds: ["daily-wellness"],
        tags: ["daily", "wellness", "featured"],
        price: 69,
        preferredPrice: 59,
        recurringPrice: 52,
        imageText: "Daily+Restore",
    }),

    createProduct({
        id: "morning-focus",
        displaySku: "TT-FOCUS-001",
        slug: "morning-focus",
        name: "Morning Focus",
        shortDescription: "Clean support for focused mornings.",
        description:
            "Morning Focus supports a clear and productive start without complicating your routine.",
        categoryIds: ["daily-wellness", "nutrition"],
        tags: ["morning", "focus", "featured"],
        price: 54,
        preferredPrice: 48,
        recurringPrice: 43,
        imageText: "Morning+Focus",
    }),

    createProduct({
        id: "night-recovery",
        displaySku: "TT-RECOVERY-001",
        slug: "night-recovery",
        name: "Night Recovery",
        shortDescription: "A calm finish to the day.",
        description:
            "Night Recovery is designed to complement evening routines and overnight recovery.",
        categoryIds: ["daily-wellness"],
        tags: ["night", "recovery"],
        price: 62,
        preferredPrice: 55,
        recurringPrice: 49,
        imageText: "Night+Recovery",
    }),

    createProduct({
        id: "daily-protein",
        displaySku: "TT-PROTEIN-001",
        slug: "daily-protein",
        name: "Daily Protein",
        shortDescription: "Smooth everyday protein in two flavours.",
        description:
            "Daily Protein provides a convenient nutritional foundation in Vanilla and Chocolate.",
        categoryIds: ["nutrition"],
        tags: ["protein", "nutrition"],
        price: 79,
        preferredPrice: 69,
        recurringPrice: 62,
        imageText: "Daily+Protein",
        includeFlavours: true,
    }),

    createProduct({
        id: "hydration-plus",
        displaySku: "TT-HYDRATE-001",
        slug: "hydration-plus",
        name: "Hydration Plus",
        shortDescription: "Convenient daily hydration support.",
        description: "Hydration Plus delivers a simple flavoured hydration option for active days.",
        categoryIds: ["nutrition"],
        tags: ["hydration", "active"],
        price: 44,
        preferredPrice: 39,
        recurringPrice: 35,
        imageText: "Hydration+Plus",
    }),

    createProduct({
        id: "complete-daily-system",
        displaySku: "TT-BUNDLE-001",
        slug: "complete-daily-system",
        name: "Complete Daily System",
        shortDescription: "Morning, daily and evening support in one system.",
        description:
            "The Complete Daily System combines Daily Restore, Morning Focus and Night Recovery.",
        categoryIds: ["bundles"],
        tags: ["bundle", "featured"],
        price: 169,
        preferredPrice: 149,
        recurringPrice: 134,
        imageText: "Complete+Daily+System",
    }),
]);

interface CreateProductInput {
    readonly id: string;
    readonly displaySku: string;
    readonly slug: string;
    readonly name: string;
    readonly shortDescription: string;
    readonly description: string;
    readonly categoryIds: readonly string[];
    readonly tags: readonly string[];
    readonly price: number;
    readonly preferredPrice: number;
    readonly recurringPrice: number;
    readonly imageText: string;
    readonly includeFlavours?: boolean;
}

function createProduct(input: CreateProductInput): CommerceProduct {
    const primaryImage = Object.freeze({
        url: `https://placehold.co/900x900?text=${input.imageText}`,
        alternativeText: input.name,
        width: 900,
        height: 900,
    });

    const offers = createOffers(input.price, input.preferredPrice, input.recurringPrice);

    const options =
        input.includeFlavours === true
            ? Object.freeze([
                  Object.freeze({
                      id: "flavour",
                      name: "Flavour",
                      values: Object.freeze([
                          Object.freeze({
                              id: "vanilla",
                              label: "Vanilla",
                          }),
                          Object.freeze({
                              id: "chocolate",
                              label: "Chocolate",
                          }),
                      ]),
                  }),
              ])
            : Object.freeze([]);

    const variants =
        input.includeFlavours === true
            ? Object.freeze([
                  Object.freeze({
                      id: `${input.id}-vanilla`,
                      displaySku: `${input.displaySku}-VAN`,
                      name: `${input.name} — Vanilla`,
                      selections: Object.freeze([
                          Object.freeze({
                              optionId: "flavour",
                              valueId: "vanilla",
                          }),
                      ]),
                      offers,
                      available: true,
                      inventoryQuantity: 120,
                  }),
                  Object.freeze({
                      id: `${input.id}-chocolate`,
                      displaySku: `${input.displaySku}-CHO`,
                      name: `${input.name} — Chocolate`,
                      selections: Object.freeze([
                          Object.freeze({
                              optionId: "flavour",
                              valueId: "chocolate",
                          }),
                      ]),
                      offers,
                      available: true,
                      inventoryQuantity: 84,
                  }),
              ])
            : Object.freeze([
                  Object.freeze({
                      id: `${input.id}-default`,
                      displaySku: input.displaySku,
                      name: input.name,
                      selections: Object.freeze([]),
                      offers,
                      available: true,
                      inventoryQuantity: 100,
                  }),
              ]);

    return Object.freeze({
        id: input.id,
        displaySku: input.displaySku,
        slug: input.slug,
        name: input.name,
        shortDescription: input.shortDescription,
        description: input.description,
        status: "active",
        primaryImage,
        images: Object.freeze([primaryImage]),
        representativeOffers: offers,
        categoryIds: Object.freeze([...input.categoryIds]),
        tags: Object.freeze([...input.tags]),
        optionGroups: options,
        variants,
        defaultVariantId: variants[0]!.id,
        extensions: Object.freeze({}),
    });
}

function createOffers(retail: number, preferred: number, recurring: number) {
    return Object.freeze([
        Object.freeze({
            purchaseType: "one-time" as const,
            price: Object.freeze({
                current: createMoney(preferred),
                compareAt: createMoney(retail),
            }),
        }),
        Object.freeze({
            purchaseType: "recurring" as const,
            price: Object.freeze({
                current: createMoney(recurring),
                compareAt: createMoney(preferred),
            }),
            frequency: Object.freeze({
                allowedDays: Object.freeze([30, 45, 60]),
                defaultDays: 30,
                defaultOptionId: "day-30",
                options: Object.freeze([
                    Object.freeze({
                        id: "day-30",
                        unit: "day" as const,
                        interval: 30,
                        days: 30,
                        description: "Every 30 days",
                    }),
                    Object.freeze({
                        id: "day-45",
                        unit: "day" as const,
                        interval: 45,
                        days: 45,
                        description: "Every 45 days",
                    }),
                    Object.freeze({
                        id: "day-60",
                        unit: "day" as const,
                        interval: 60,
                        days: 60,
                        description: "Every 60 days",
                    }),
                ]),
            }),
        }),
    ]);
}

function createMoney(amount: number) {
    return Object.freeze({
        amount,
        currencyCode: "NZD",
        formatted: `$${amount.toFixed(2)}`,
    });
}
